import { Group, Stack, Title, Text, Button, Paper, ThemeIcon } from '@mantine/core';
import { IconInbox } from '@tabler/icons-react';
import type { ReactNode } from 'react';

/** Props for {@link EmptyState}. */
export interface EmptyStateProps {
  /**
   * Optional resource-typed SVG illustration (typically one of the
   * `IllustrationXxx` components from `@/components/ds`). When provided
   * it renders ABOVE the title and REPLACES the default `<ThemeIcon>`
   * container — pick one or the other, not both.
   */
  illustration?: ReactNode;
  /**
   * Icon rendered inside the large `<ThemeIcon>` container.
   * Defaults to `<IconInbox>` from @tabler/icons-react. Ignored when
   * `illustration` is set.
   */
  icon?: ReactNode;
  /** Primary empty-state heading (h3). */
  title: string;
  /** Optional explanatory copy rendered below the title in a dimmed, centred style. */
  body?: string;
  /**
   * Optional call-to-action button configuration.
   * When provided, renders a filled `<Button>` below the body copy.
   */
  cta?: {
    label: string;
    onClick: () => void;
    /** Optional left-section icon for the CTA button. */
    icon?: ReactNode;
  };
  /**
   * Optional second, lower-emphasis action rendered beside `cta` (a
   * `variant="default"` button, never filled — there is only ever one
   * primary action). Absent when there is nothing distinct for it to do;
   * do not invent a secondary action just to fill this slot.
   */
  secondaryCta?: {
    label: string;
    onClick: () => void;
    icon?: ReactNode;
  };
}

/**
 * EmptyState — centred placeholder shown when a list or section has no data.
 *
 * Shell: any.
 *
 * Renders inside a `<Paper p='xl' withBorder>` to give it clear visual
 * containment. When an `illustration` is supplied (recommended), it shows
 * a resource-typed SVG above the title instead of the generic `<ThemeIcon>`
 * with an icon. The two slots are mutually exclusive — `illustration` wins.
 *
 * `secondaryCta`, when provided, renders beside `cta` as a `variant="default"`
 * button — never a second filled button, since there is only ever one primary
 * action (vendor#425).
 */
export function EmptyState({
  illustration,
  icon,
  title,
  body,
  cta,
  secondaryCta,
}: EmptyStateProps) {
  return (
    <Paper p="xl" withBorder ta="center">
      <Stack align="center" gap="sm">
        {illustration ?? (
          <ThemeIcon size={48} variant="light">
            {icon ?? <IconInbox size={24} />}
          </ThemeIcon>
        )}
        <Title order={3}>{title}</Title>
        {body != null && (
          <Text c="dimmed" ta="center" maw={420}>
            {body}
          </Text>
        )}
        {(cta != null || secondaryCta != null) && (
          <Group gap="xs">
            {cta != null && (
              <Button leftSection={cta.icon} onClick={cta.onClick}>
                {cta.label}
              </Button>
            )}
            {secondaryCta != null && (
              <Button
                variant="default"
                leftSection={secondaryCta.icon}
                onClick={secondaryCta.onClick}
              >
                {secondaryCta.label}
              </Button>
            )}
          </Group>
        )}
      </Stack>
    </Paper>
  );
}
